import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Building2, UserPlus, Trash2, Key, Clock, CheckCircle2,
  XCircle, Search, Users, Send, Eye, EyeOff, AlertCircle
} from 'lucide-react';

const formatCurrency = (amount, currency = 'XOF') =>
  `${Number(amount || 0).toLocaleString('fr-FR')} ${currency === 'XOF' ? 'FCFA' : currency}`;

const STATUS_MAP = {
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approuve: { label: 'Approuvé', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const OwnerAccess = () => {
  const [accounts, setAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    username: '', password: '', client_id: '', property_ids: []
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, reqRes, cliRes, propRes] = await Promise.all([
        api.get('/owner-accounts'),
        api.get('/withdrawal-requests'),
        api.get('/clients'),
        api.get('/properties')
      ]);
      setAccounts(accRes.data);
      setRequests(reqRes.data);
      const allClients = Array.isArray(cliRes.data) ? cliRes.data : [];
      const bailleurs = allClients.filter(c => c.client_type === 'bailleur');
      setClients(bailleurs);
      setProperties(Array.isArray(propRes.data) ? propRes.data : []);
      // Auto-select GAB'S SERVICES HABITAT as default bailleur
      const gabs = bailleurs.find(c => c.name && c.name.toUpperCase().includes("GAB"));
      if (gabs) {
        setForm(f => ({ ...f, client_id: f.client_id || gabs.id }));
      } else if (bailleurs.length === 1) {
        setForm(f => ({ ...f, client_id: f.client_id || bailleurs[0].id }));
      }
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.username) {
      toast.error('Veuillez saisir un identifiant de connexion');
      return;
    }
    if (!form.password) {
      toast.error('Veuillez saisir un mot de passe');
      return;
    }
    if (!form.client_id) {
      toast.error('Aucun bailleur selectionne');
      return;
    }
    if (form.property_ids.length === 0) {
      toast.error('Veuillez selectionner au moins un bien');
      return;
    }
    try {
      await api.post('/owner-accounts', form);
      toast.success('Compte proprietaire cree');
      setShowCreateModal(false);
      setForm({ username: '', password: '', client_id: '', property_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de ${name} ?`)) return;
    try {
      await api.delete(`/owner-accounts/${id}`);
      toast.success('Compte supprimé');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Approuver cette demande de retrait ?')) return;
    try {
      await api.post(`/withdrawal-requests/${requestId}/approve`);
      toast.success('Demande approuvée et retrait effectué');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    try {
      await api.post(`/withdrawal-requests/${selectedRequest}/reject`, { reason: rejectReason });
      toast.success('Demande refusée');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const toggleProperty = (pid) => {
    setForm(f => ({
      ...f,
      property_ids: f.property_ids.includes(pid)
        ? f.property_ids.filter(id => id !== pid)
        : [...f.property_ids, pid]
    }));
  };

  const pendingRequests = requests.filter(r => r.status === 'en_attente');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="owner-access-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#1B3A4B]" /> Accès Propriétaires
          </h1>
          <p className="text-gray-500 mt-1">Gérez les comptes propriétaires et les demandes de retrait</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="create-owner-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Nouveau compte
        </Button>
      </div>

      <Tabs defaultValue={pendingRequests.length > 0 ? 'requests' : 'accounts'}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="accounts">Comptes ({accounts.length})</TabsTrigger>
          <TabsTrigger value="requests" className="relative">
            Demandes
            {pendingRequests.length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{pendingRequests.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-3 mt-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>

          {accounts.filter(a => !searchTerm || a.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) || a.username?.toLowerCase().includes(searchTerm.toLowerCase())).map(acc => (
            <Card key={acc.id} data-testid={`owner-account-${acc.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1B3A4B]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#1B3A4B]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{acc.client_name}</p>
                    <p className="text-xs text-gray-500">Identifiant: <span className="font-mono">{acc.username}</span></p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(acc.properties || []).map(p => (
                        <Badge key={p.id} className="bg-gray-100 text-gray-600 text-xs">{p.title}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={acc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{acc.is_active ? 'Actif' : 'Inactif'}</Badge>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(acc.id, acc.client_name)} data-testid={`delete-owner-${acc.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {accounts.length === 0 && (
            <Card><CardContent className="p-8 text-center text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              Aucun compte propriétaire
            </CardContent></Card>
          )}
        </TabsContent>

        {/* Withdrawal Requests Tab */}
        <TabsContent value="requests" className="space-y-3 mt-3">
          {pendingRequests.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700">{pendingRequests.length} demande(s) en attente de validation</span>
            </div>
          )}

          {requests.map(req => {
            const st = STATUS_MAP[req.status] || STATUS_MAP.en_attente;
            return (
              <Card key={req.id} className={req.status === 'en_attente' ? 'border-amber-200' : ''} data-testid={`request-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{formatCurrency(req.amount)}</span>
                        <Badge className={st.color + ' text-xs'}>{st.label}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{req.owner_name} — {req.property_title}</p>
                      <p className="text-xs text-gray-500">Ref: {req.reference} | {req.payment_method} | {new Date(req.created_at).toLocaleDateString('fr-FR')}</p>
                      {req.notes && <p className="text-xs text-gray-400 mt-1 italic">"{req.notes}"</p>}
                      {req.admin_response && <p className="text-xs text-red-500 mt-1">Motif: {req.admin_response}</p>}
                    </div>
                    {req.status === 'en_attente' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleApprove(req.id)} data-testid={`approve-${req.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approuver
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => { setSelectedRequest(req.id); setShowRejectModal(true); }} data-testid={`reject-${req.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {requests.length === 0 && (
            <Card><CardContent className="p-8 text-center text-gray-400">
              <Send className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              Aucune demande de retrait
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Owner Account Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Créer un compte propriétaire</DialogTitle>
            <DialogDescription>Le propriétaire pourra consulter ses soldes et demander des retraits</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Proprietaire (Bailleur)</Label>
              {clients.length === 0 ? (
                <div className="mt-1 p-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                  Aucun bailleur trouve. Creez d'abord un client de type "bailleur".
                </div>
              ) : clients.length === 1 ? (
                <div className="mt-1 p-2.5 bg-[#1B3A4B]/5 border border-[#1B3A4B]/20 rounded-md text-sm font-medium text-[#1B3A4B]">
                  {clients[0].name}
                </div>
              ) : (
                <Select value={form.client_id} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="owner-bailleur-select">
                    <SelectValue placeholder="Selectionnez un bailleur" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Identifiant de connexion</Label>
              <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="Ex: prop_dupont" data-testid="owner-username-input" />
            </div>
            <div>
              <Label>Mot de passe</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mot de passe" data-testid="owner-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Biens du propriétaire</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1 mt-1">
                {properties.map(p => (
                  <label key={p.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${form.property_ids.includes(p.id) ? 'bg-[#1B3A4B]/5 border border-[#1B3A4B]/30' : ''}`}>
                    <input type="checkbox" checked={form.property_ids.includes(p.id)} onChange={() => toggleProperty(p.id)} className="accent-[#1B3A4B]" />
                    <span className="text-sm">{p.title}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">{form.property_ids.length} bien(s) sélectionné(s)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="confirm-create-owner">Créer le compte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={(open) => { setShowRejectModal(open); if (!open) setSelectedRequest(null); }}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <Label>Motif du refus (optionnel)</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Indiquez la raison..." rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Annuler</Button>
            <Button onClick={handleReject} variant="destructive" data-testid="confirm-reject">Refuser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerAccess;
